import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Pencil, Plus, Trash2, FileText, Users, MessageSquare, Eye, Mail, Flame, Bold, Italic, Heading2, Quote, ImagePlus, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, categoryLabel } from "../lib/categories";
import Reveal from "../components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";

const EMPTY_FORM = { title: "", category: "fashion", excerpt: "", body: "", image_url: "", tags: "", find_it: "", featured: false, published: true };

export default function AdminPage() {
  const { user, openAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [opinions, setOpinions] = useState([]);
  const [tab, setTab] = useState("overview");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const [dispatchBusy, setDispatchBusy] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [dailyBusy, setDailyBusy] = useState(false);
  const [contentRuns, setContentRuns] = useState([]);
  const [affiliate, setAffiliate] = useState(null);
  const [dirEntries, setDirEntries] = useState([]);

  const sendDispatch = async () => {
    setDispatchBusy(true);
    try {
      const { data } = await api.post("/admin/dispatch");
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDispatchBusy(false);
    }
  };

  const sendDaily = async () => {
    setDailyBusy(true);
    try {
      const { data } = await api.post("/admin/generate-daily", null, { params: { force: true } });
      toast.success(data.message);
      setTimeout(load, 8000);
      setTimeout(load, 60000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDailyBusy(false);
    }
  };

  const insertMarkup = (before, after = "", block = false) => {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = form.body.substring(start, end);
    let next;
    if (block) {
      next = `${form.body.substring(0, start)}\n\n${before}${sel || after}\n\n${form.body.substring(end)}`;
    } else {
      next = `${form.body.substring(0, start)}${before}${sel || "text"}${after}${form.body.substring(end)}`;
    }
    setForm({ ...form, body: next });
    requestAnimationFrame(() => ta.focus());
  };

  const isAdmin = user && user.role === "admin";

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/admin/dispatches").then((r) => setDispatches(r.data)).catch(() => {});
    api.get("/admin/content-runs").then((r) => setContentRuns(r.data)).catch(() => {});
    api.get("/admin/directory").then((r) => setDirEntries(r.data)).catch(() => {});
    api.get("/admin/affiliate-stats").then((r) => setAffiliate(r.data)).catch(() => {});
    api.get("/opinions", { params: { sort: "latest", limit: 100 } }).then((r) => setOpinions(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (user === null) {
    return <div className="min-h-[60vh] flex items-center justify-center"><p className="font-mono text-ash text-xs uppercase tracking-[0.3em] animate-pulse">Loading…</p></div>;
  }

  if (!isAdmin) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-32 text-center">
        <p className="font-display font-extrabold text-4xl uppercase">Editors only</p>
        <p className="text-ash mt-4">The Studio is restricted to the editorial desk.</p>
        {!user && (
          <button data-testid="admin-signin-prompt" onClick={() => openAuth("login")}
            className="mt-8 bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium px-7 py-4 rounded-full hover:bg-porcelain transition-colors">
            Sign In
          </button>
        )}
      </div>
    );
  }

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title, category: a.category, excerpt: a.excerpt, body: a.body,
      image_url: a.image_url, tags: (a.tags || []).join(", "),
      find_it: (a.find_it || []).map((f) => [f.name, f.note, f.url].join(" | ")).join("\n"),
      featured: !!a.featured, published: !!a.published,
    });
    setFormOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      find_it: form.find_it.split("\n").map((l) => l.split("|").map((s) => s.trim())).filter((p) => p[0] && p[2]).map((p) => ({ name: p[0], note: p[1] || "", url: p[2] })),
    };
    try {
      if (editing) {
        await api.put(`/articles/${editing}`, payload);
        toast.success("Story updated.");
      } else {
        await api.post("/articles", payload);
        toast.success("Story published to the desk.");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (a) => {
    try {
      await api.put(`/articles/${a.id}`, {
        title: a.title, category: a.category, excerpt: a.excerpt, body: a.body,
        image_url: a.image_url, tags: a.tags || [], featured: !!a.featured, published: !a.published,
      });
      toast.success(a.published ? "Story unpublished." : "Story published.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}" permanently?`)) return;
    try {
      await api.delete(`/articles/${a.id}`);
      toast.success("Story deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeOpinion = async (o) => {
    if (!window.confirm(`Remove "${o.title}" from Voices?`)) return;
    try {
      await api.delete(`/opinions/${o.id}`);
      toast.success("Opinion removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleDirApprove = async (d) => {
    try {
      const { data } = await api.post(`/admin/directory/${d.id}/approve`);
      toast.success(data.status === "approved" ? `${d.company} is live on the wall.` : `${d.company} moved back to pending.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeDirEntry = async (d) => {
    if (!window.confirm(`Delete the listing for "${d.company}"?`)) return;
    try {
      await api.delete(`/admin/directory/${d.id}`);
      toast.success("Listing deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const statCards = stats ? [
    { label: "Stories", value: stats.articles, icon: FileText, color: "text-kinetic" },
    { label: "Total Views", value: stats.views, icon: Eye, color: "text-cobalt" },
    { label: "Members", value: stats.members, icon: Users, color: "text-kinetic" },
    { label: "Voices", value: stats.opinions, icon: Flame, color: "text-crimson" },
    { label: "Comments", value: stats.comments, icon: MessageSquare, color: "text-cobalt" },
    { label: "Subscribers", value: stats.subscribers, icon: Mail, color: "text-kinetic" },
  ] : [];

  return (
    <div data-testid="admin-cms-dashboard" className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-3">The Studio</p>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">Editor's Desk</h1>
          </div>
          <button data-testid="admin-create-article-button" onClick={openNew}
            className="bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium px-6 py-3.5 rounded-full flex items-center gap-2 hover:bg-porcelain transition-colors w-fit">
            <Plus size={15} /> New Story
          </button>
        </div>
      </Reveal>

      <div className="flex gap-2 mb-10 border-b border-white/10">
        {[["overview", "Overview"], ["articles", "Stories"], ["voices", "Moderation"], ["directory", "Directory"]].map(([key, label]) => (
          <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
            className={`text-xs font-mono uppercase tracking-[0.18em] px-5 py-3 border-b-2 -mb-px transition-colors ${tab === key ? "border-kinetic text-kinetic" : "border-transparent text-ash hover:text-porcelain"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="border border-white/10 bg-surface p-5 flex flex-col justify-between gap-4">
              <div>
                <p className="font-display font-bold text-lg">The Weekly Dispatch</p>
                <p className="text-ash text-sm mt-1">
                  Auto-sends every Friday morning.{" "}
                  {dispatches.length > 0
                    ? `Last sent: ${dispatches[0].week} to ${dispatches[0].sent} subscriber${dispatches[0].sent === 1 ? "" : "s"}.`
                    : "Never sent yet."}
                </p>
              </div>
              <button data-testid="admin-send-dispatch-button" onClick={sendDispatch} disabled={dispatchBusy}
                className="bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium px-6 py-3.5 rounded-full flex items-center gap-2 hover:bg-porcelain transition-colors disabled:opacity-50 shrink-0 w-fit">
                <Send size={14} /> {dispatchBusy ? "Sending…" : "Send Dispatch Now"}
              </button>
            </div>
            <div className="border border-white/10 bg-surface p-5 flex flex-col justify-between gap-4">
              <div>
                <p className="font-display font-bold text-lg">The Daily Engine</p>
                <p className="text-ash text-sm mt-1">
                  Writes 9 fresh pieces every morning at 05:00 GMT — reviews, narratives and trivia games, one per section.{" "}
                  {contentRuns.length > 0 && contentRuns[0].status === "done"
                    ? `Last run: ${contentRuns[0].date} · ${contentRuns[0].created} pieces.`
                    : contentRuns.length > 0 ? `Last run: ${contentRuns[0].date} · ${contentRuns[0].status}.` : "First run pending."}
                </p>
              </div>
              <button data-testid="admin-generate-daily-button" onClick={sendDaily} disabled={dailyBusy}
                className="bg-crimson text-porcelain font-mono text-xs uppercase tracking-[0.18em] font-medium px-6 py-3.5 rounded-full flex items-center gap-2 hover:bg-kinetic hover:text-obsidian transition-colors disabled:opacity-50 shrink-0 w-fit">
                <Flame size={14} /> {dailyBusy ? "Starting…" : "Generate Today's Content"}
              </button>
            </div>
          </div>
          {affiliate && (
            <div data-testid="affiliate-stats-panel" className="border border-white/10 bg-surface p-5 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div>
                  <p className="font-display font-bold text-lg">Affiliate Clicks</p>
                  <p className="text-ash text-sm mt-1">Amazon link taps across every story and snippet.</p>
                </div>
                <div className="flex gap-8">
                  <div>
                    <p className="font-display font-black text-2xl text-kinetic">{affiliate.total}</p>
                    <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-ash">All time</p>
                  </div>
                  <div>
                    <p className="font-display font-black text-2xl text-kinetic">{affiliate.last_7_days}</p>
                    <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-ash">Last 7 days</p>
                  </div>
                </div>
              </div>
              {affiliate.top_products.length === 0 ? (
                <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] py-3">No clicks yet — they appear here as readers tap product links.</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {affiliate.top_products.map((p) => (
                    <div key={p.product} data-testid="affiliate-product-row" className="py-3 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.product}</p>
                        {p.articles[0] && (
                          <Link to={`/article/${p.articles[0]}`} className="text-[10px] font-mono text-ash hover:text-kinetic transition-colors truncate">
                            via {p.articles[0].replace(/-/g, " ")}
                          </Link>
                        )}
                      </div>
                      <span className="font-display font-black text-xl text-kinetic shrink-0">{p.clicks}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {statCards.map((s) => (
              <div key={s.label} data-testid="admin-analytics-stat-card" className="border border-white/10 bg-surface p-5">
                <s.icon size={18} className={`${s.color} mb-4`} />
                <p className="font-display font-black text-3xl">{s.value.toLocaleString()}</p>
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ash mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "articles" && (
        <div data-testid="admin-articles-table" className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-white/10 bg-surface text-left">
                <th className="px-5 py-4 text-[10px] font-mono uppercase tracking-[0.2em] text-ash font-medium">Title</th>
                <th className="px-5 py-4 text-[10px] font-mono uppercase tracking-[0.2em] text-ash font-medium">Section</th>
                <th className="px-5 py-4 text-[10px] font-mono uppercase tracking-[0.2em] text-ash font-medium">Views</th>
                <th className="px-5 py-4 text-[10px] font-mono uppercase tracking-[0.2em] text-ash font-medium">Published</th>
                <th className="px-5 py-4 text-[10px] font-mono uppercase tracking-[0.2em] text-ash font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="px-5 py-4">
                    <Link to={`/article/${a.slug}`} className="font-medium hover:text-kinetic transition-colors line-clamp-1">{a.title}</Link>
                    <span className="text-[10px] font-mono text-ash">{format(new Date(a.created_at), "dd MMM yyyy")}</span>
                  </td>
                  <td className="px-5 py-4 text-xs font-mono text-ash uppercase">{categoryLabel(a.category)}</td>
                  <td className="px-5 py-4 text-xs font-mono text-ash">{a.views || 0}</td>
                  <td className="px-5 py-4">
                    <Switch data-testid="admin-publish-status-toggle" checked={!!a.published} onCheckedChange={() => togglePublish(a)} />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <button data-testid="admin-edit-article-button" onClick={() => openEdit(a)}
                        className="p-2 text-ash hover:text-kinetic border border-white/10 hover:border-kinetic/50 transition-colors"><Pencil size={14} /></button>
                      <button data-testid="admin-delete-article-button" onClick={() => removeArticle(a)}
                        className="p-2 text-ash hover:text-crimson border border-white/10 hover:border-crimson/50 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "voices" && (
        <div className="space-y-3">
          {opinions.map((o) => (
            <div key={o.id} data-testid="admin-opinion-row" className="border border-white/10 bg-surface p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium line-clamp-1">{o.title}</p>
                <p className="text-[10px] font-mono text-ash uppercase tracking-[0.15em] mt-1.5">
                  @{o.author_name} · {categoryLabel(o.category)} · {o.upvotes} votes · {o.comment_count} replies
                </p>
              </div>
              <button data-testid="admin-delete-opinion-button" onClick={() => removeOpinion(o)}
                className="p-2 text-ash hover:text-crimson border border-white/10 hover:border-crimson/50 transition-colors shrink-0"><Trash2 size={14} /></button>
            </div>
          ))}
          {opinions.length === 0 && <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] py-10 text-center">No voices yet.</p>}
        </div>
      )}

      {tab === "directory" && (
        <div className="space-y-3">
          {dirEntries.map((d) => (
            <div key={d.id} data-testid="admin-directory-row" className="border border-white/10 bg-surface p-4 flex items-center gap-4">
              {d.card_image ? (
                <img src={d.card_image} alt="" className="w-24 h-14 object-cover border border-white/10 shrink-0 bg-obsidian" />
              ) : (
                <span className="w-24 h-14 border border-white/10 shrink-0 bg-obsidian flex items-center justify-center text-ash text-[9px] font-mono">NO CARD</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-medium line-clamp-1">{d.company}</p>
                <p className="text-[10px] font-mono text-ash uppercase tracking-[0.15em] mt-1">
                  {d.category} · {d.location} ·{" "}
                  <span className={d.status === "approved" ? "text-kinetic" : "text-crimson"}>{d.status}</span> ·{" "}
                  <span data-testid="admin-directory-paid-badge" className={d.payment_status === "paid" ? "text-kinetic" : "text-ash"}>
                    {d.payment_status === "paid" ? "£10 paid" : "unpaid"}
                  </span>
                </p>
              </div>
              <a href={d.website} target="_blank" rel="noopener noreferrer" data-testid="admin-directory-website-link"
                className="p-2 text-ash hover:text-kinetic border border-white/10 hover:border-kinetic/50 transition-colors shrink-0">
                <Eye size={14} />
              </a>
              <button data-testid="admin-directory-approve-button" onClick={() => toggleDirApprove(d)}
                className={`text-[10px] font-mono uppercase tracking-[0.15em] px-4 py-2.5 rounded-full border transition-colors shrink-0 ${d.status === "approved" ? "border-kinetic/50 text-kinetic" : "border-white/15 text-ash hover:text-kinetic hover:border-kinetic/50"}`}>
                {d.status === "approved" ? "Live" : "Approve"}
              </button>
              <button data-testid="admin-directory-delete-button" onClick={() => removeDirEntry(d)}
                className="p-2 text-ash hover:text-crimson border border-white/10 hover:border-crimson/50 transition-colors shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {dirEntries.length === 0 && (
            <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] py-10 text-center">No directory submissions yet.</p>
          )}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="admin-article-form-modal" className="bg-surface border-white/10 text-porcelain sm:max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">
              {editing ? "Edit story" : "New story"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="flex flex-col gap-4">
            <input data-testid="admin-article-title-input" value={form.title} required minLength={4}
              onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Headline"
              className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors" />
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button type="button" key={c.slug} data-testid={`admin-article-category-${c.slug}`} onClick={() => setForm({ ...form, category: c.slug })}
                  className={`text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors ${form.category === c.slug ? "bg-kinetic text-obsidian border-kinetic" : "border-white/10 text-ash hover:text-porcelain"}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <input data-testid="admin-article-image-input" value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL (https://…)"
              className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors" />
            <textarea data-testid="admin-article-excerpt-input" value={form.excerpt} rows={2} maxLength={400}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Standfirst / excerpt"
              className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm resize-y transition-colors" />
            <div>
              <div data-testid="editor-toolbar" className="flex flex-wrap gap-1.5 mb-2">
                <button type="button" data-testid="editor-bold-button" onClick={() => insertMarkup("**", "**")} title="Bold"
                  className="p-2.5 border border-white/10 text-ash hover:text-kinetic hover:border-kinetic/50 transition-colors"><Bold size={14} /></button>
                <button type="button" data-testid="editor-italic-button" onClick={() => insertMarkup("*", "*")} title="Italic"
                  className="p-2.5 border border-white/10 text-ash hover:text-kinetic hover:border-kinetic/50 transition-colors"><Italic size={14} /></button>
                <button type="button" data-testid="editor-heading-button" onClick={() => insertMarkup("## ", "", true)} title="Subheading"
                  className="p-2.5 border border-white/10 text-ash hover:text-kinetic hover:border-kinetic/50 transition-colors"><Heading2 size={14} /></button>
                <button type="button" data-testid="editor-quote-button" onClick={() => insertMarkup("> ", "", true)} title="Pull quote"
                  className="p-2.5 border border-white/10 text-ash hover:text-kinetic hover:border-kinetic/50 transition-colors"><Quote size={14} /></button>
                <button type="button" data-testid="editor-image-button" onClick={() => insertMarkup("![caption](https://image-url)", "", true)} title="Inline image"
                  className="p-2.5 border border-white/10 text-ash hover:text-kinetic hover:border-kinetic/50 transition-colors"><ImagePlus size={14} /></button>
              </div>
              <textarea ref={bodyRef} data-testid="admin-article-body-input" value={form.body} required minLength={20} rows={10}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder={"Body — separate paragraphs with a blank line.\n\nFormatting: **bold**, *italic*, ## subheading, > pull quote, ![caption](image-url) for inline images."}
                className="w-full bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm leading-relaxed resize-y transition-colors" />
            </div>
            <input data-testid="admin-article-tags-input" value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags, comma separated"
              className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors" />
            <textarea data-testid="admin-article-findit-input" value={form.find_it} rows={3}
              onChange={(e) => setForm({ ...form, find_it: e.target.value })}
              placeholder={"Where to find it — one per line:\nScan Computers | GPUs and full rigs | https://www.scan.co.uk"}
              className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm resize-y transition-colors" />
            <div className="flex items-center gap-8">
              <label className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.15em] text-ash cursor-pointer">
                <Switch data-testid="admin-article-featured-toggle" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
                Featured
              </label>
              <label className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.15em] text-ash cursor-pointer">
                <Switch data-testid="admin-article-published-toggle" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
                Published
              </label>
            </div>
            <button data-testid="admin-article-save-button" type="submit" disabled={busy}
              className="bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium py-4 rounded-full hover:bg-porcelain transition-colors disabled:opacity-50">
              {busy ? "Saving…" : editing ? "Save changes" : "Publish story"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
